import React, { Component } from "react";
import { observer } from "mobx-react";
import "./VideoSync.scss";
import {
  Trans,
  Translation,
  useTranslation,
  withTranslation
} from "react-i18next";
import VideoSyncState from "./VideoSyncState";
import MusicTrackState from "../MusicTrack/MusicTrackState";
import dragPercentage from "../../utils/dragPercentage";
import Fader from "./Fader/Fader";
import WaveformDisplay from "../MusicTrack/WaveformDisplay/WaveformDisplay";
import Popup from "../Popup/Popup";
import playSvg from "./icons/play-solid.svg?raw";
import pauseSvg from "./icons/pause-solid.svg?raw";
import closeSvg from "./icons/close2.svg?raw";
import replaySvg from "./icons/replay2.svg?raw";
import uploadSvg from "./icons/upload-solid-v3.svg?raw";

const videoplaceholder = require("./VideosyncPlaceholder.jpg");

/**
 * @typedef {Object} VideoSyncProps
 * @property {VideoSyncState} store - The video sync state store
 */

/**
 * @typedef {Object} VideoSyncReactState
 * @property {boolean} dragOver - Whether drag over state is active
 */

@observer
class VideoSync extends Component {
  /** @type {HTMLElement | null} */
  dropRef;

  constructor() {
    super();

    this.state = {
      dragOver: false
    };
  }

  componentDidMount() {
    window.addEventListener("dragover", this.handleDragOver, false);
    window.addEventListener("dragleave", this.handleDragLeave, false);
    window.addEventListener("drop", this.handleFileSelect, false);
  }

  componentWillUnmount() {
    window.removeEventListener("dragover", this.handleDragOver, false);
    window.removeEventListener("dragleave", this.handleDragLeave, false);
    window.removeEventListener("drop", this.handleFileSelect, false);
  }

  /**
   * @param {HTMLElement | null} ref
   */
  setDropRef = (ref) => {
    this.dropRef = ref;
  };

  handleFileSelect = (evt) => {
    evt.stopPropagation();
    evt.preventDefault();

    const files = evt.dataTransfer.files;
    if (files[0].type.startsWith("video/")) {
      this.props.store.setVideo(URL.createObjectURL(files[0]), files[0].name);
      this.props.store.searchState.logEvent(
        `video-sync`,
        "loaded",
        `${files[0].name}`
      );
    } else {
      alert(`Unsupported file type. Must be a video. Got '${files[0].type}'`);
    }

    this.setState({ dragOver: false });
  };

  handleDragOver = (evt) => {
    evt.stopPropagation();
    evt.preventDefault();
    evt.dataTransfer.dropEffect = "copy"; // Explicitly show this is a copy.
    this.setState({ dragOver: true });
  };

  handleDragLeave = () => {
    this.setState({ dragOver: false });
  };

  /**
   * @param {HTMLVideoElement | null} setVideoRef
   */
  setVideoRef = (ref) => {
    if (ref == null) return;
    this.props.store.setVideoRef(ref);
  };

  render() {
    // Access currentLanguage to ensure re-render on language change
    const lang = this.props.store.currentLanguage;

    let videoListingModal = null;
    const rows = [];

    if (
      this.props.store.videosInAccount.length ||
      this.props.store.filteredVideosInAccount.length
    ) {
      const videos = this.props.store.filteredVideosInAccount.length
        ? this.props.store.filteredVideosInAccount
        : this.props.store.videosInAccount;

      for (let i = 0; i < videos.length; i += 1) {
        const video = videos[i];
        let link = "Processing...";
        if (video.status) {
          link = (
            <div
              className={"link-video"}
              onClick={e => {
                console.log(`${video.url}`);
                this.props.store.setVideo(video.url, video.name);
                this.props.store.addVideoToPlaylist(video.id);
                this.props.store.close();
              }}
            >
              Attach to playlist
            </div>
          );
        }

        rows.push(
          <tr key={video.id}>
            <td>{video.name}</td>
            <td>{video.created_on}</td>
            <td>{link}</td>
          </tr>
        );
      }
    }

    if (this.props.store.showingVideoListing) {
      videoListingModal = (
        <Popup onClose={this.props.store.close}>
          <div className={"MyAccount-list-top-banner"}>
            <div className={"MyAccount-list-top-banner-title"}>
              my uploaded video library
            </div>
            <div
              className={"Popup-banner-close"}
              onClick={e => {
                e.preventDefault();
                e.stopPropagation();
                this.props.store.hideVideoList();
              }}
            />
          </div>
          <div className={"MyAccount-header"}>
            <input
              className={`Search-filter`}
              ref={r => {
                if (this.props.store.showingPanelFirst === 0) {
                  if (r != null) r.focus();
                  this.props.store.showSearchFilter();
                  this.props.store.filterVideos("");
                }
              }}
              onChange={e => {
                this.props.store.filterVideos(e.target.value);
              }}
              onKeyDown={e => {
                e.stopPropagation();
              }}
              placeholder={"Type to filter saved video syncs"}
            />
          </div>
          <div className={"MyAccount-list-table-holder"}>
            <table className={"MyAccount-list-table VideoSync-list-table"}>
              <thead>
                <tr>
                  <td className={"MyAccount-name-column"}>video filename</td>
                  <td className={"MyAccount-name-column"}>upload date</td>
                  <td className={"MyAccount-name-column"}>status</td>
                </tr>
              </thead>
              <tbody>{rows}</tbody>
            </table>
          </div>
          <div className={"Search-by-track-popup-button-holder"}>
            <button
              className={"Popup-button"}
              onClick={e => {
                e.preventDefault();
                e.stopPropagation();
                this.props.store.close();
              }}
            >
              <Trans>close</Trans>
            </button>
          </div>
        </Popup>
      );
    }

    let syncTip = null;
    if (
      this.props.store.searchState.showSyncTip &&
      !sessionStorage.getItem(`tip::videosynctip`)
    ) {
      syncTip = (
        <div className={"Music-play-tip Video-Sync-Tip"}>
          Move the red triangle
          <br />
          to where you would like
          <br />
          the music to start
        </div>
      );
    }

    let resetVideoX = null;
    let playPause = (
      <span
        className={``}
        dangerouslySetInnerHTML={{ __html: pauseSvg }}
        onClick={this.props.store.togglePause}
      />
    );
    
    let uploadNewVideo = (
      <span
        className={``}
        dangerouslySetInnerHTML={{ __html: uploadSvg }}
        onClick={this.props.store.loadNewVideo}
      />
    );

    if (!this.props.store.searchState.timeline.playing) {
      playPause = (
        <span
          className={``}
          dangerouslySetInnerHTML={{ __html: playSvg }}
          onClick={this.props.store.togglePause}
        />
      );
    }

    if (this.props.store.url != null) {
      resetVideoX = (
        <div ref={`535.5`} className={`VideoSync-Screen-Controls`}>
          {playPause}
          <span
            className={``}
            dangerouslySetInnerHTML={{ __html: replaySvg }}
            onClick={this.props.store.searchState.timeline.resetFromVideoSync}
          />
          {uploadNewVideo}
          <span
            className={``}
            dangerouslySetInnerHTML={{ __html: closeSvg }}
            onClick={this.props.store.resetVideo}
          />
        </div>
      );
    }

    let content;
    if (this.props.store.url == null) {
      content = (
        <div className={"VideoSync-instructions"}>
          <p>
            <b>
              <Trans i18nKey="videosync_a">video sync tool</Trans>
            </b>
          </p>

          <p className={`medium`}>
            <Trans i18nKey="videosync_b">
            drag a video to preview or click here to select a file from your computer
            </Trans>
          </p>
          <p className={`small`}>
            <Trans i18nKey='videosync_c'>
              this video will <strong>not</strong> be uploaded to any server or
              cloud
            </Trans>
          </p>
          {/* <p>tip: you can drag and drop movies here</p> */}
        </div>
      );
    } else {
      let videoHeight = 287;
      if (this.dropRef != null) {
        videoHeight = this.dropRef.getBoundingClientRect().height;
      }

      content = (
        <video
          height={videoHeight}
          key={this.props.store.url}
          ref={this.setVideoRef}
        >
          <source src={this.props.store.url} />
        </video>
      );
    }

    /*
    let nameTrack = null;
    let videoDuration = this.props.store.getVideoDuration();
    if (this.props.store.filename != null) {
      nameTrack = (
        <div className={'VideoSync-name-track'}
             onMouseDown={dragPercentage(this.props.store.seekPercentage, 'VideoSync-name-track')}>
          {this.props.store.filename}
          <div className={'VideoSync-name-track-length'}>
            {videoDuration}
          </div>
        </div>
      );
    }
    */

    let audioTrack = (<div
      className={"VideoSync-audio-track-outer-holder"}
      key={`temp-track`}
    />);
    if (
      MusicTrackState.globallyActiveTrack != null &&
      !MusicTrackState.globallyActiveTrack.loadingAudio
    ) {
      const activeTrack = MusicTrackState.globallyActiveTrack;

      if (this.props.store.filename != null) {
        const offset =
          this.props.store.searchState.timeline.audioOffset /
          this.props.store.searchState.timeline.videoDuration;
        audioTrack = (
          <div
            className={"VideoSync-audio-track-outer-holder"}
            key={activeTrack.music.id}
          >
            <div className={"VideoSync-audio-track-inner-holder"}>
              <div
                className={"VideoSync-audio-track"}
                style={{
                  width: `${(100 *
                    this.props.store.searchState.timeline.trackDuration) /
                    this.props.store.searchState.timeline.videoDuration}%`,
                  left: `${offset * 100}%`
                }}
                onMouseUp={this.props.store.manualDragStop}
                onMouseDown={dragPercentage(
                  newOffset =>
                    this.props.store.searchState.timeline.setAudioOffset(
                      newOffset *
                        this.props.store.searchState.timeline.videoDuration
                    ),
                  "VideoSync-audio-track-inner-holder",
                  offset
                )}
              >
                {/*
                <img
                  className={'VideoSync-audio-track-waveform'}
                  src={`https://search.ascheandspencer.com/assets/waveforms/${MusicTrackState.globallyActiveTrack.music.waveforms}.png`}
                />
                */}
                <div className={"VideoSync-audio-track-waveform"}>
                  <WaveformDisplay
                    ref={this.props.store.setWaveformRef}
                    audioBuffer={activeTrack.audioBuffer}
                    trackId={activeTrack.music.id}
                    cache={activeTrack.waveformDisplayCache}
                  />
                </div>
              </div>
              <div
                className={"VideoSync-audio-track-bg"}
                onMouseDown={dragPercentage(
                  this.props.store.searchState.timeline.seekVideoPercentage,
                  "VideoSync-audio-track-inner-holder"
                )}
              />
            </div>
            <div className={"VideoSync-audio-track-holder-left"} />
            <div className={"VideoSync-audio-track-holder-right"} />
          </div>
        );
      } else {
        /*
        audioTrack = (
          <div className={'VideoSync-audio-track-outer-holder'} key={activeTrack.music.id}>
            <div className={'VideoSync-audio-track-inner-holder'}>
              <div className={'VideoSync-audio-track'}
                   style={{
                     width: '100%',
                     left: '0'
                   }}>
                <img className={'VideoSync-audio-track-waveform'} src={'https://search.ascheandspencer.com/assets/waveforms/'+MusicTrackState.globallyActiveTrack.music.waveforms+'.png'}/>
              </div>
            </div>
            <div className={'VideoSync-audio-track-holder-left'}/>
            <div className={'VideoSync-audio-track-holder-right'}/>
          </div>
        );
        */
      }
    }

    let progressIndicator = null;
    let anchorIndicator = null;
    if (this.props.store.videoLoaded) {
      progressIndicator = (
        <div
          className={"VideoSync-progress-bar"}
          onClick={this.props.store.searchState.hideSyncTip}
          onMouseDown={dragPercentage(
            this.props.store.searchState.timeline.seekVideoPercentage,
            "VideoSync-track-holder"
          )}
          style={{
            left: `${this.props.store.searchState.timeline.videoPercentage *
              100}%`
          }}
        >
          <div
            className={`VideoSync-progress-bar-inner${
              this.props.store.searchState.timeline.videoPercentage === 0 ||
              this.props.store.searchState.timeline.videoPercentage === 1.0
                ? " VideoSync-progress-bar-inner-oob"
                : ""
            }`}
          />
        </div>
      );
      anchorIndicator = (
        <div
          className={"VideoSync-progress-bar-anchor"}
          onClick={this.props.store.searchState.hideSyncTip}
          onMouseDown={dragPercentage(
            this.props.store.searchState.timeline.setVideoAnchor,
            "VideoSync-track-holder"
          )}
          onMouseUp={this.props.store.manualDragStop}
          style={{
            left: `${this.props.store.searchState.timeline
              .videoAnchorPercentage * 100}%`
          }}
        />
      );
    }

    return (
      <div className={"VideoSync"}>
        {videoListingModal}
        {syncTip}
        <div className={"VideoSync-screen-holder"}>
          {resetVideoX}
          <div
            className={`VideoSync-screen${
              this.state.dragOver ? " VideoSync-screen-dragover" : ""
            }`}
            ref={this.setDropRef}
            onClick={this.props.store.clickScreen}
          >
            {content}
          </div>
          <div className={`VideoSync-movingbackground`}>
            <video
              playsInline
              autoPlay
              muted
              loop={false}
              poster={videoplaceholder}
              onEnded={e => {
                const video = e.target;
                if (!video.loopCount) {
                  video.loopCount = 1;
                  const playPromise = video.play();
                  if (playPromise !== undefined) {
                    playPromise.catch(error => {
                      // Ignore AbortError - it's expected when pause is called quickly
                      if (error.name !== "AbortError") {
                        console.error("Video play error:", error);
                      }
                    });
                  }
                } else if (video.loopCount === 1) {
                  video.pause();
                }
              }}
            >
              <source
                src="https://search.ascheandspencer.com/32372512-hd.mp4"
                type="video/mp4"
              />
            </video>
          </div>
          <div className={"VideoSync-track-holder"}>
            {progressIndicator}
            {anchorIndicator}
            {/* nameTrack */}
            {audioTrack}
          </div>
        </div>
        <div className={"VideoSync-slider-container"}>
          <div className={"VideoSync-slider-spacer"} />
          <div className={"VideoSync-slider-holder"}>
            <Fader
              name={<Trans>music level</Trans>}
              volume={this.props.store.searchState.musicVolume}
              setVolume={this.props.store.searchState.setMusicVolume}
              highlightSlider={false}
            />
          </div>
          <div className={"VideoSync-slider-holder"}>
            <Fader
              name={<Trans>video level</Trans>}
              volume={this.props.store.videoAudioLevel}
              setVolume={this.props.store.setVideoAudioLevel}
              highlightSlider={false}
            />
          </div>
          <div className={"VideoSync-slider-spacer"} />
        </div>
      </div>
    );
  }
}

export default withTranslation()(VideoSync);
